// ----------------------------------------------------------------------
// <copyright file="Izenda.Config.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
import ensure from 'ensure';
import { mergeRouteConfig } from 'logic/data/Izenda.RouteConstants';
import { merge, isNil, isFunction, intersection } from 'lodash/fp';
import Moment from 'moment';

let webApiUrl = '';
let baseUrl = '/';
let rootPath = '';
let cssFile = '';
let routeIntegration = true;
let routes = {
  Settings: 'settings',
  ReportDesigner: 'reportdesigner',
  Dashboard: 'dashboard',
  Report: 'report',
  Explore: 'explore',
  ReportViewer: 'reportviewer',
  Viewer: 'viewer',
  Account: 'account',
  MyProfile: 'myprofile'
};

let routesMerge = mergeRouteConfig(routes);
let timeoutInSeconds = 3600;
let printingTimeout = 60 * 1000;
let printingRenderingTimeout = 6 * 1000;
let defaultLanguage = 'en-US';
let needToEncodeUrl = true;
let onReceiveUnauthorizedResponse = message => {};
let uiPreferences = {
  ReportFilterSectionExpanded: true,
  RequiredFilterSectionExpanded: true
};
let AjaxSettings = {
  
};
let minDate = new Date('1/1/1970');
let maxDate = Moment().add(20, 'y').toDate();
let googleMapJSApiUrl = 'https://maps.googleapis.com/maps/api/js';
let googleMapJSApiVersion = 'quarterly';

let errorHandler = () => {};

/**
 * Option object
 */
export default {
  /**
   * The URL of Izenda API. It needs have trailing slash.
   */
  get WebApiUrl() {
    return webApiUrl;
  },
  /**
   * The base URL of all Izenda report pages
   */
  get BaseUrl() {
    return baseUrl;
  },
  /**
   * The relative path of Izenda resources
   */
  get RootPath() {
    return rootPath;
  },

  get CssFile() {
    return cssFile;
  },

  /**
   * Indicates the app using integrated routing system. For example, if integrated, the routing
   * url of Izenda will be resolved by ?source= query string.
   * Customer frontend app should ignore this setting
   */
  get Integration() {
    return routeIntegration;
  },

  get Routes() {
    return routes;
  },

  get RoutesMerge() {
    return routesMerge;
  },

  get Timeout() {
    return timeoutInSeconds;
  },

  get PrintingTimeout() {
    return printingTimeout;
  },

  get PrintingRenderingTimeout() {
    return printingRenderingTimeout;
  },

  /**
   * Default language key of Izenda. The key setup here must match key in language file
   */
  get DefaultLanguage() {
    return defaultLanguage;
  },

  /**
   * This option is set in integration mode only. Default is true -> always encode url
   * For example, AngularJS automatically encodes the hash of URL. It causes the hash part
   * is encoded twice. Therefore, this setting is for ignoring url encoding by Izenda.
   */
  get NeedToEncodeUrl() {
    return needToEncodeUrl;
  },

  /**
   * In integration mode, when a request receives 401 status code -> this method is run
   */
  get OnReceiveUnauthorizedResponse() {
    return onReceiveUnauthorizedResponse;
  },
  /**
   * Defines all default value of UI appearance preferences
   */
  get UIPreferences() {
    return uiPreferences;
  },

   /**
   * Defines all default value of Ajax Settings
   */
  get AjaxSettings() {
    return AjaxSettings;
  },
  



  /**
   * Minimum date that is use for datetime picker.
   */
  get MinDate() {
    return minDate;
  },

  /**
   * Maximum date that is use for datetime picker.
   */
  get MaxDate() {
    return maxDate;
  },

  /**
   * Google Map Javascript API Url.
   */
  get GoogleMapJSApiUrl() {
    return googleMapJSApiUrl;
  },

  /**
   * Google Map Javascript API version.
   */
  get GoogleMapJSApiVersion() {
    return googleMapJSApiVersion;
  },

  /**
   * User defined error handler
   */
  get ErrorHandler() {
    return errorHandler;
  },

  /**
   * Set options
   */
  set(options) {
    ensure(options, 'Invalid options');
    ensure(options.WebApiUrl, 'WebApiUrl must be set');
    webApiUrl = isFunction(options.WebApiUrl) ? options.WebApiUrl() : options.WebApiUrl;
    routes = merge(routes, options.Routes);
    routesMerge = mergeRouteConfig(routes);
    baseUrl = options.BaseUrl || baseUrl;
    rootPath = options.RootPath || rootPath;
    cssFile = options.CssFile || cssFile;
    routeIntegration = isNil(options.Integration) ? routeIntegration : options.Integration;
    timeoutInSeconds = options.TimeOut || timeoutInSeconds;
    printingTimeout = options.PrintingTimeout || printingTimeout;
    printingRenderingTimeout = options.PrintingRenderingTimeout || printingRenderingTimeout;
    needToEncodeUrl = isNil(options.NeedToEncodeUrl) ? needToEncodeUrl : options.NeedToEncodeUrl;
    onReceiveUnauthorizedResponse = !isFunction(options.OnReceiveUnauthorizedResponse)
      ? onReceiveUnauthorizedResponse
      : options.OnReceiveUnauthorizedResponse;
    uiPreferences = isNil(options.UIPreferences) ? uiPreferences : merge(uiPreferences, options.UIPreferences);
    AjaxSettings = isNil(options.AjaxSettings) ? AjaxSettings :  (intersection(Object.keys(options.AjaxSettings),["WebApiUrl","BaseUrl","RootPath","CssFile","Routes","Settings", "New","Dashboard","Report","ReportViewer","ReportViewerPopup","Viewer","Timeout","OnReceiveUnauthorizedResponse"]).length === 0) ?  merge(AjaxSettings,options.AjaxSettings) : AjaxSettings;
    minDate = options.minDate || minDate;
    maxDate = options.maxDate || maxDate;
    googleMapJSApiUrl = options.googleMapJSApiUrl || googleMapJSApiUrl;
    googleMapJSApiVersion = options.googleMapJSApiVersion || googleMapJSApiVersion;
    errorHandler = isFunction(options.ErrorHandler) ? options.ErrorHandler : errorHandler;
  }
};



// WEBPACK FOOTER //
// ./ui/Izenda.Config.js